import { format } from "date-fns";
import { ThreadMessage } from "./Conversation";
import classNames from "classnames";
import ConversationMessage from "./ConversationMessage";
import { memo, useCallback } from "react";

interface ConversationMessageGroupProps {
  date?: string;
  messagesInDay: ThreadMessage[];
}

const ConversationMessageGroup = ({
  date,
  messagesInDay,
}: ConversationMessageGroupProps) => {
  const messagesGroupedBySenderAndHour = messagesInDay.reduce(
    (acc, message) => {
      if (message === undefined) {
        console.error(
          "message is undefined in messagesGroupedBySenderAndHour",
          date,
        );
      }
      const lastGroup = acc[acc.length - 1];

      const currentTime = new Date(message.sentAt);
      const isWithinHour =
        lastGroup &&
        Math.abs(+currentTime - +new Date(lastGroup[0].sentAt)) <
          60 * 60 * 1000;

      if (
        lastGroup &&
        lastGroup[0].senderId === message.senderId &&
        isWithinHour
      ) {
        lastGroup.push(message);
      } else {
        acc.push([message]);
      }

      return acc;
    },
    [] as ThreadMessage[][],
  );

  // Memoize the render function for each message
  const renderMessage = useCallback((message: ThreadMessage) => {
    return (
      <ConversationMessage
        className={message.className}
        key={message.chatMessageId}
        content={message.content}
        contentColor={message.contentColor}
        format={message.format}
        attachments={message.attachments}
        DisplayComponent={message.DisplayComponent}
      />
    );
  }, []);

  return (
    <div className={"tw-flex tw-flex-col tw-items-center tw-gap-2"}>
      {date && (
        <div
          className={
            "tw-m-3 tw-justify-center tw-rounded-full tw-bg-neutral-50 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-neutral-400"
          }
        >
          {date}
        </div>
      )}
      <div className={"tw-flex tw-w-full tw-flex-col tw-gap-1 tw-py-1"}>
        <div className={"tw-flex tw-flex-col tw-gap-4"}>
          {messagesGroupedBySenderAndHour.map((messages, i) => (
            <div key={`message-group-${i}`} className="tw-flex tw-gap-2">
              <div>{messages[0].Avatar()}</div>
              <div className={"tw-flex tw-flex-col"}>
                <div>
                  <span className={"tw-mr-1.5 tw-font-semibold"}>
                    {messages[0].senderDisplayName}
                  </span>
                  <span
                    className={classNames(
                      "tw-text-xs",
                      "tw-font-medium",
                      "tw-uppercase",
                      "tw-text-neutral-400",
                      "tw-tracking-wider",
                    )}
                  >
                    {format(messages[0].sentAt, "h:mm a")}
                  </span>
                </div>

                <div className={"tw-w-full"}>{messages.map(renderMessage)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for React.memo
const areEqual = (
  prevProps: ConversationMessageGroupProps,
  nextProps: ConversationMessageGroupProps,
) => {
  if (prevProps.date !== nextProps.date) return false;
  if (prevProps.messagesInDay.length !== nextProps.messagesInDay.length)
    return false;

  return prevProps.messagesInDay.every((prevMessage, index) => {
    const nextMessage = nextProps.messagesInDay[index];
    return (
      prevMessage.chatMessageId === nextMessage.chatMessageId &&
      prevMessage.content === nextMessage.content &&
      prevMessage.format === nextMessage.format &&
      prevMessage.senderId === nextMessage.senderId &&
      prevMessage.senderDisplayName === nextMessage.senderDisplayName &&
      +prevMessage.sentAt === +nextMessage.sentAt &&
      JSON.stringify(prevMessage.attachments) ===
        JSON.stringify(nextMessage.attachments)
    );
  });
};

export default memo(ConversationMessageGroup, areEqual);
